import { type ExpenseListQuery, RECURRING_STATUSES, type RecurringStatus } from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'

export const PAGE_SIZES = [25, 50, 100] as const

const pageSize = z
  .number()
  .refine((n): n is (typeof PAGE_SIZES)[number] => (PAGE_SIZES as readonly number[]).includes(n))
  .optional()
  .catch(undefined)

const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

// ── transfers ────────────────────────────────────────────────────────────────

export const transferSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize,
})
export type TransferSearch = z.infer<typeof transferSearchSchema>
export interface TransferListParams {
  page: number
  pageSize: number
}

export function toTransferListQuery(search: TransferSearch): TransferListParams {
  return { page: search.page ?? 1, pageSize: search.pageSize ?? PAGE_SIZES[0] }
}

// ── expenses ─────────────────────────────────────────────────────────────────

export const EXPENSE_SORT_FIELDS = ['expenseDate', 'number', 'total'] as const
type ExpenseSortField = (typeof EXPENSE_SORT_FIELDS)[number]
export type ExpenseSort = ExpenseSortField | `-${ExpenseSortField}`

export function isExpenseSort(value: string): value is ExpenseSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (EXPENSE_SORT_FIELDS as readonly string[]).includes(field)
}

/** The expenses list lives in the URL; invalid values fall back to the default. */
export const expenseSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize,
  sort: z.string().refine(isExpenseSort).optional().catch(undefined),
  q: z.string().trim().max(200).optional().catch(undefined),
  from: isoDate,
  to: isoDate,
  categoryId: z.uuid().optional().catch(undefined),
  treasuryAccountId: z.uuid().optional().catch(undefined),
  status: z.enum(['POSTED', 'VOID']).optional().catch(undefined),
  /** Expenses tagged to this project. */
  projectId: z.uuid().optional().catch(undefined),
})
export type ExpenseSearch = z.infer<typeof expenseSearchSchema>

/** Newest expenses first (the API's default too). */
export const EXPENSE_DEFAULT_SORT = '-expenseDate'

export type ExpenseListParams = Omit<ExpenseListQuery, 'page' | 'pageSize'> & {
  page: number
  pageSize: number
}

export function toExpenseListQuery(search: ExpenseSearch): ExpenseListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? EXPENSE_DEFAULT_SORT,
    q: search.q || undefined,
    from: search.from,
    to: search.to,
    categoryId: search.categoryId,
    treasuryAccountId: search.treasuryAccountId,
    status: search.status,
    projectId: search.projectId,
  }
}

/** New expense: `?projectId=` (opened from a project) starts it on that project. */
export const newExpenseSearchSchema = z.object({ projectId: z.uuid().optional().catch(undefined) })

// ── recurring expenses ───────────────────────────────────────────────────────

export const RECURRING_SORT_FIELDS = ['name', 'nextRunDate', 'amount'] as const
type RecurringSortField = (typeof RECURRING_SORT_FIELDS)[number]
export type RecurringSort = RecurringSortField | `-${RecurringSortField}`

export function isRecurringSort(value: string): value is RecurringSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (RECURRING_SORT_FIELDS as readonly string[]).includes(field)
}

export const recurringSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize,
  sort: z.string().refine(isRecurringSort).optional().catch(undefined),
  q: z.string().trim().max(200).optional().catch(undefined),
  status: z.enum(RECURRING_STATUSES).optional().catch(undefined),
})
export type RecurringSearch = z.infer<typeof recurringSearchSchema>

export interface RecurringListParams {
  page: number
  pageSize: number
  sort: string
  q: string | undefined
  status: RecurringStatus | undefined
}

/** The next date first (the API's default too). */
export const RECURRING_DEFAULT_SORT = 'nextRunDate'

export function toRecurringListQuery(search: RecurringSearch): RecurringListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? RECURRING_DEFAULT_SORT,
    q: search.q || undefined,
    status: search.status,
  }
}

/** New recurring expense: `?fromExpense=` ("Make this recurring") starts it from that expense. */
export const newRecurringSearchSchema = z.object({ fromExpense: z.uuid().optional().catch(undefined) })
